import type { TaskPriorityLevels } from "@/enums/communicationLogs";
import type { CustomAttributeStatusOption } from "@/enums/customAttributes";
import type { Entity, Relation } from "@/enums/entityProfiles";
import type {
  ModifierKeys,
  ShortcutAction,
  ShortcutContext,
} from "@/enums/keyboardShortcuts";
import type { OfferProductType } from "@/enums/offers";
import type { IDiscoveryCallQuestion } from "@/models/applications";
import type {
  ArrayWithStringValues,
  IDashboardSettings,
} from "@/models/common";
import type { ConsentTypes } from "@/models/consentTemplate";
import type { ContractCustomAttributeContext } from "@/models/contractBuilder";
import type {
  DataOrchestrationMasterStatus,
  DataOrchestrationMasterStatusType,
  DataOrchestrationStatus,
  DataOrchestrationTrigger,
} from "@/models/orchestration";
import type { StipDefinition } from "@/models/stips";
import type { IWidgetTemplate } from "@/models/widgetTemplates";
import type {
  IWorkflowTemplateDefinition,
  WidgetDefinition,
  WorkflowFieldDefinition,
  WorkflowTemplateCategoriesDefinition,
} from "@/models/workflows";
import type { Ref } from "vue";

export interface IBorrowerPlatformTemplatesOptions {
  tab_types: Record<string, string>;
  tab_visibilities: Record<string, string>;
  tab_positions: Record<string, string>;
  block_types: Record<string, string>;
  alignments: Record<string, string>;
  welcome_page_types: Record<string, string>;
  tile_action_types: Record<string, string>;
  tile_headings: Record<string, string>;
  tile_image_types: Record<string, string>;
  tile_layouts: Record<string, string>;
}

export type Role =
  | "super admin"
  | "admin"
  | "underwriter"
  | "funding advisor"
  | "client admin"
  | "user"
  | "funder admin"
  | "funder user"
  | "client underwriter"
  | "client funding advisor"
  | "analyst"
  | "client analyst";

export type BusinessCreditService =
  | "experian_uccs"
  | "experian_bankruptcies"
  | "experian_judgments"
  | "experian_liens"
  | "experian_intelliscore"
  | "experian_fsr"
  | "experian_commercial_collections"
  | "experian_credit_status"
  | "experian_legal_collections"
  | "experian_trades"
  | "experian_corporate_registrations"
  | "experian_business_contacts"
  | "experian_business_facts"
  | "experian_fraud_shields"
  | "experian_business_match"
  | "experian_business_quick_search"
  | "dnb_cer_l1"
  | "dnb_ci_l2"
  | "dnb_pi_l3"
  | "dnb_fi_l2"
  | "dnb_fi_l3"
  | "dnb_fi_l4"
  | "dnb_dti_l1"
  | "dnb_bm_l1"
  | "middesk"
  | "persona"
  | "ocrolus_cfa"
  | "sentilink"
  | "sentilink_completion"
  | "codat"
  | "clear"
  | "ekata"
  | "inscribe"
  | "lexis_nexis_kyc"
  | "ntropy"
  | "mx"
  | "heron"
  | "scorely"
  | "railz"
  | "plaid"
  | "plaid_asset_report"
  | "plaid_bank_data"
  | "socure_kyc"
  | "socure_fraud"
  | "socure_dv"
  | "sentilink_ssn_completion"
  | "sentilink_dob_completion"
  | "experian_bop_blended_account_review"
  | "experian_bop_blended_prequalification"
  | "experian_bop_commercial_collections"
  | "experian_bop_commercial_factoring"
  | "experian_bop_commercial_insurance"
  | "experian_bop_commercial_lending_to_a_sole_prop"
  | "experian_bop_commercial_lending_with_a_pg"
  | "experian_bop_merchant_acquisition"
  | "lexis_nexis_liens_and_judgment_search"
  | "lexis_nexis_judgments_report"
  | "clear_id_confirm_business"
  | "clear_id_confirm_person"
  | "clear_risk_inform_business_report"
  | "clear_risk_inform_business_search"
  | "clear_risk_inform_person_search"
  | "lexis_nexis_bankruptcy_search"
  | "lexis_nexis_bankruptcy_report"
  | "clear_risk_inform_person_report"
  | "clear_court_search"
  | "equifax_business_principal_report"
  | "experian_bop_merchant_cash_advance"
  | "lexis_nexis_small_business_credit_score"
  | "lexis_nexis_small_business_blended_credit_score"
  | "vouched_verification"
  | "vouched_cross_check";

export type SubmissionMethod = 1 | 2 | 3 | 4;
export type ProductType = 1 | 2 | 3 | 4 | 5 | 9;
export type ScorecardProductType = ProductType | 7 | 8;
export type RiskTier = 1 | 2 | 3 | 4 | 5 | 6;
export type RiskTierKey =
  | "super_prime"
  | "near_prime"
  | "prime"
  | "sub_prime"
  | "deep_prime"
  | "dead";

export interface IIndustry {
  id: number;
  label: string;
  sub_industry_types: ISubIndustry[];
}

export interface ISubIndustry {
  id: number;
  label: string;
}

// This type is used for modified options from options store
// when we need to add the industry parent_id to sub_industry
// to make selection process simpler for DnbUpdateRecord
export type SubIndustryWithParent = ISubIndustry & { parent_id: number };

export interface INumberOfEmployee {
  id: number;
  label: string;
}

export interface IOptionsState {
  all: IOptions | null;
  application: IOptionsApplication | null;
  workflow_templates: IOptionsWorkflowTemplates | null;
  dynamic_fields: DynamicFieldOptions | null;
  dashboardSettings: IDashboardSettings | null;
  contract_templates: ContractTemplateOptions | null;
  attributeOptions: Record<string, unknown>;
  offerTemplateOptions: OfferOptions | null;
  version: string | null;
  naicsCodesOptions: Record<string, string>;
  borrowerPlatformTemplatesOptions: IBorrowerPlatformTemplatesOptions | null;
  dataOrchestration: {
    statuses: Record<string, DataOrchestrationStatus>;
    triggers: Record<string, DataOrchestrationTrigger>;
    master_statuses: Record<
      DataOrchestrationMasterStatusType,
      DataOrchestrationMasterStatus
    >;
  } | null;
}

export interface DynamicFieldOptions {
  fields: DynamicFieldOption[];
}

export interface DynamicFieldOption {
  name: string;
  tag: string;
  allowed_templates: string[];
}

export type ContractTemplateRecipientRole = "signer" | "client" | "guarantor";

export interface ContractTemplateOptions {
  contract_statuses: string[];
  fields: ContractTemplateFieldAndCategory[];
  latest_schema_version: string;
  recipient_roles: ContractTemplateRecipientRole[];
}

export interface ContractTemplateFieldAndCategory {
  category: string;
  fields: ContractTemplateField[];
}

export interface ContractTemplateField {
  description: string;
  id: string;
  tab_label: string;
  is_readonly: boolean;
  name: string;
  type: string;
  context?: ContractCustomAttributeContext;
}

export interface OfferOptions {
  field_types: string[];
  latests_schema_version: string;
  current_schema_version: string;
  meaningful_fields: Record<string, unknown>;
  fallback_templates: {
    templates: Record<string, unknown>[];
    product_mapping: Record<OfferProductType, string>;
  };
}

export interface FileTypeCollection {
  dir: string;
  name: string;
  types: string[];
  categories: {
    type: string;
    name: string;
  };
}

export enum CheckoutApplicationFieldType {
  addresses = "addresses",
  currency = "currency",
  date = "date",
  float = "float",
  integer = "integer",
  options = "options",
  subOptions = "sub_options",
  percentage = "percentage",
  string = "string",
  url = "url",
  text = "text",
  boolean = "boolean",
  email = "email",
  pii = "pii",
  phoneNumbers = "phone_numbers",
  phoneNumbersWithAdditionalPhoneNumber = "phone_number_with_additional_phone_numbers",
  ssnItin = "ssn_itin",
}

export type CheckoutFieldBlock = {
  id: string;
  name: string;
  group: string;
  description: string | null;
  is_array: boolean;
  fields: WorkflowFieldDefinition[];
};

export type CheckoutOptions = {
  definition: {
    application_blocks: CheckoutFieldBlock[];
    field_types: Record<string, CheckoutApplicationFieldType[]>;
    owners_required: Record<string, string>;
    step_types: Record<string, string>;
    table_blocks: CheckoutFieldBlock[];
  };
  latest_schema_version: string;
};

export type AiAgentsOptions = {
  definition: {
    agent_types: Record<string, string>;
  };
  latest_schema_version: string;
};

export type IUserStatuses = ["Pending", "Approved", "Declined"];

export interface INotification {
  name: string;
  id: number;
}

export interface INotificationTypes {
  1: INotification[];
  2: INotification[];
  3: INotification[];
  4: INotification[];
  5: INotification[];
  6: INotification[];
  7: INotification[];
  8: INotification[];
  9: INotification[];
}

export type AvailableServices =
  | "Middesk"
  | "Socure"
  | "Ekata"
  | "Sentilink"
  | "Dun & Bradstreet"
  | "Ocrolus"
  | "Experian"
  | "Inscribe"
  | "Persona"
  | "Plaid";
export interface IExpandedServiceData {
  service: AvailableServices;
  baseDataExpanded: boolean;
  additionalDataExpanded: boolean;
}

export interface IPlacementDeclineSubReason {
  id: number;
  label: string;
  parent_id: number;
}

export enum SmartviewNotificationTypes {
  Mail = "mail",
}

export interface FormulaParameter {
  name: string;
  description: string | null;
  optional: boolean;
  default: number | string | null;
  variadic: boolean;
  relates_to?: string;
}
export interface FormulaOption {
  name: string;
  aliases: string[];
  parameters: FormulaParameter[];
  precedence: number;
  usage: string | null;
}

export interface AttributeOptions {
  functions: FormulaOption[];
  latest_schema_version: string;
}

export type IOptionsApplication = Partial<{
  statuses: IApplicationStatus[];
  finished_types: Record<string, string>;
  signature_statuses: Record<string, string>;
  dead_reasons: Record<string, string>;
  dead_sub_reasons: Record<string, string>;
  closing_statuses: Record<string, string>;
  bc_data_statuses: Record<string, string>;
  eq_rental_approvals: Record<string, string>;
  sources: Record<string, string>;
  filters: Record<
    string,
    {
      data: Record<string, string> | null;
      type: string;
    }
  >;
  outcomes: Record<string, string>;
  deal_modes: Record<string, string>;
  naics_codes: Record<string, string>;
  filter_notification_types: SmartviewNotificationTypes[];
}>;

export type BillingType = "none" | "direct" | "inbound" | "outgoing";

export interface BillingTypeDefinition {
  description: string;
  id: BillingType;
  name: string;
}

export interface IOptionsWorkflowTemplates {
  billing_types: BillingTypeDefinition[];
  categories: WorkflowTemplateCategoriesDefinition[];
  definition: IWorkflowTemplateDefinition;
  statuses: Record<string, string>;
  types: Record<string, string>;
  widgets: Record<string, WidgetDefinition>;
  workflow_segments: MultiSelectOption[];
}

export interface RiskTierOption {
  indicator: RiskTier;
  priority: number;
  name: string;
}

export interface IOptions {
  application: IOptionsApplication;
  attributes: AttributeOptions;
  default_widget_template: IWidgetTemplate;
  funder_risk_tiers: RiskTierOption[];
  funder_contact_roles: ArrayWithStringValues;
  asset_types: Record<string, string>;
  products: Record<string, string>;
  score_cards_products: Record<string, string>;
  product_fields: Record<string, Record<string, Record<string, string>>>;
  product_fields_dictionary: Record<string, Record<string, string>>;
  product_funding_fields: Record<
    string,
    Record<string, Record<string, string>>
  >;
  countries: Record<string, string>;
  ecommerce_types: Record<string, string>;
  account_receivables: Record<string, string>;
  accounting_software: Record<string, string>;
  business_entity_types: Record<string, string>;
  credit_scores: Record<string, string>;
  communication: Communication;
  funds_needed_times: Record<string, string>;
  industry_types: IIndustry[];
  industry_type: IIndustry;
  inventories: Record<string, string>;
  number_of_employees: INumberOfEmployee[];
  payroll_software: Record<string, string>;
  monthly_revenue: Record<string, string>;
  use_of_funds: Record<string, string>;
  times_in_businesses: Record<string, string>;
  time_zones: Record<string, string>;
  file_types: Record<string, FileTypeCollection>;
  roles: Record<string, Role>;
  placement: {
    methods: Record<string, string>;
    statuses: Record<string, string>;
    decline_reasons: Record<string, string>;
    decline_subreasons: IPlacementDeclineSubReason[];
    new_decline_reasons: Record<string, string>;
  };
  stips: StipDefinition[];
  offers: {
    contact_via: IContactVia;
    statuses: Record<string, string>;
    payment_periods: Record<string, string>;
    base_rate: Record<number, string>;
    interest_charged: Record<number, string>;
    term_length_timeframe: Record<number, string>;
    time_to_funding_timeframe: Record<number, string>;
  };
  docusign_templates: Record<string, string>;
  states: Record<string, string>;
  cities: Record<string, string>;
  subscription_types: Record<string, string>;
  client_product_types: Record<string, string>;
  business_credit_services: IBusinessCreditServices;
  experian_bop_consumer_score_models: IExperianBopConsumerScoreModels;
  notification_types: INotificationTypes;
  citizenship: Record<string, string>;
  discovery_call_questions: IDiscoveryCallQuestion[];
  languages: Record<string, string>;
  insurance_types: Record<string, string>;
  user: {
    status: IUserStatuses;
    keyboard_shortcuts: Record<string, KeyboardShortcutOption>;
  };
  workflow_templates: IOptionsWorkflowTemplates;
  expandedServiceData: IExpandedServiceData[];
  discovery_call_default_questions: string[];
  profile_management: {
    relationships: RelationOption[];
  };
  attribute: {
    statuses: Record<CustomAttributeStatusOption, CustomAttributeStatusOption>;
  };
  pii_mapping: Record<number, string>;
  loan_servicers: Record<string, string>[];
  debt_types: Record<number, string>;
  task: {
    priority_levels: TaskPriorityLevels[];
  };
  email_template_dynamic_fields: EmailTemplateDynamicField[];
  transunion_models: Record<string, TransUnionModelValue>;
  services_allowed_for_updating_enrichment_response: string[];
  workflow_orchestration_log_statuses: Record<string, string>;
  line_types: Record<string, string>;
}

export interface KeyboardShortcutOption {
  action: string;
  action_name: string;
  action_string: ShortcutAction;
  modifiers: ModifierKeys[];
  keys: string[];
  context: ShortcutContext;
  is_custom?: boolean;
}

export interface UserKeyboardShortcut extends KeyboardShortcutOption {
  key: string;
  shortcutTriggered?: Ref<boolean>;
}

export interface KeyboardShortcutUpdate {
  key: string;
  action: string;
  action_string: ShortcutAction;
  modifiers?: string[];
  keys?: string[];
  valid?: boolean;
  conflict?: boolean;
}

export interface KeyboardShortcutPayloadEntry {
  action: string;
  action_string: ShortcutAction;
  modifiers?: ModifierKeys[];
  keys?: string[];
  action_name: string;
  context: ShortcutContext;
}

export interface KeyboardShortcutPayload {
  shortcuts: Record<string, KeyboardShortcutPayloadEntry>;
}

export interface TransUnionModelValue {
  name: string;
  value: string;
}
export interface EmailTemplateDynamicField {
  name: string;
  tag: string;
  allowed_templates: string[];
}

export interface Communication {
  call_statuses: string[];
  call_dispositions: string[];
}
export interface RelationOptionElement {
  id: Relation;
  name: string;
  name_plural: string;
  supported_entities: Entity[];
  children?: RelationOptionElementChild[];
}

export interface RelationOptionElementChild {
  id: string;
  name: string;
  name_plural: string;
}

export interface RelationOption {
  type: RelationOptionElement;
  inverse: RelationOptionElement;
}

export interface IContactVia {
  type: string;
  created_at: string;
}

export type Servicer = "mca_track" | "Canopy";

export interface LoanServicer {
  conditions: string;
  name: string;
  type: Servicer;
}

export type BusinessCreditServiceObjectValue = Record<string, string>;

export type BusinessCreditServiceKey =
  | "Ekata"
  | "Experian"
  | "Experian Business Owner Profile"
  | "Experian Business Owner Profile Premier Attributes"
  | "D&B"
  | "Middesk"
  | "Persona"
  | "Ocrolus CFA"
  | "Equifax"
  | "LexisNexis"
  | "Clear"
  | "SentiLink"
  | "Ntropy"
  | "Inscribe"
  | "Socure"
  | "Plaid"
  | "Codat";

export type IBusinessCreditServices = {
  [K in BusinessCreditServiceKey]: BusinessCreditServiceObjectValue;
};

export interface IExperianBopConsumerScoreModels {
  fico8: string;
  fico9: string;
  fico_advanced2: string;
  fico_v2: string;
  vantage4: string;
}

export type IApplicationStatus = { id: number; status: string };

export type UseOfFundsOption = (id?: number) => string | Record<string, string>;

export type MultiSelectOption = {
  id: string;
  name: string;
};

export interface ConsentOptions {
  definition: {
    consent_types: ConsentTypes;
  };
  latest_schema_version: string;
}
